import React, { useState } from 'react';
import axios from 'axios';
import { MoreVertical, Edit, Trash2, Play, Clock, CheckCircle, XCircle, Activity, BarChart3, Mail, MessageCircle, Settings, Power } from 'lucide-react';

const formatLastChecked = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const checked = new Date(date);
  const diff = now - checked;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
};

function SyntheticCard({ test, onEdit, onDelete, onRun, onViewResults, onToggleActive, darkMode = false }) {
  const [showMenu, setShowMenu] = useState(false);

  const {
    id,
    name,
    description,
    isActive,
    intervalSeconds,
    lastRun,
    results,
    notifyEmail,
    notifyTelegram,
  } = test;

  const lastResult = results?.[0] || null;
  const lastStatus = lastResult?.status || 'unknown';
  const lastError = lastResult?.error || null;

  const greenColor = '#95DE1A';
  const redColor = '#FD0E0B';
  const orangeColor = '#F97316';
  const grayColor = '#9CA3AF';

  const getStatusColor = () => {
    if (!isActive) return grayColor;
    switch (lastStatus) {
      case 'success': return greenColor;
      case 'failed': return redColor;
      default: return orangeColor;
    }
  };

  const getStatusIcon = () => {
    if (!isActive) return <Clock className="h-4 w-4" />;
    switch (lastStatus) {
      case 'success': return <CheckCircle className="h-4 w-4" />;
      case 'failed': return <XCircle className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusText = () => {
    if (!isActive) return 'Inactive';
    switch (lastStatus) {
      case 'success': return 'Success';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const formatInterval = (seconds) => {
    if (seconds < 60) return `${seconds} sec`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
    return `${Math.floor(seconds / 3600)} hour`;
  };

  const handleViewResults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/synthetic-tests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onViewResults(response.data);
    } catch (error) {
      console.error('Failed to fetch test details:', error);
      alert('Sonuçlar yüklenirken hata oluştu.');
    }
  };

  return (
    <div className={`rounded-3xl shadow-lg transition-all duration-500 hover:shadow-xl hover:-translate-y-1 group ${
      darkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div
        className="px-6 py-4 border-b rounded-t-3xl"
        style={{
          backgroundColor: isActive 
            ? (lastStatus === 'success' ? `${greenColor}20` : lastStatus === 'failed' ? `${redColor}20` : `${orangeColor}20`)
            : (darkMode ? '#374151' : '#F3F4F6'),
          borderColor: darkMode ? '#374151' : '#E5E7EB'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor() }}></div>
            <span className="font-semibold text-sm" style={{ color: getStatusColor() }}>
              {getStatusText()}
            </span>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-xl hover:bg-black/10 transition-colors"
            >
              <MoreVertical className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} strokeWidth={2} fill="none" />
            </button>
            {showMenu && (
              <div className={`absolute right-0 top-10 w-44 rounded-2xl shadow-xl border py-2 z-20 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <button
                  onClick={() => { onToggleActive(id, isActive); setShowMenu(false); }}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${
                    isActive 
                      ? (darkMode ? 'text-orange-400 hover:bg-gray-700' : 'text-orange-600 hover:bg-orange-50')
                      : (darkMode ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-green-50')
                  }`}
                >
                  <Power className="h-4 w-4" strokeWidth={2} fill="none" />
                  <span>{isActive ? 'Deactivate' : 'Activate'}</span>
                </button>
                <button
                  onClick={() => { onEdit(test); setShowMenu(false); }}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${
                    darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  <Edit className="h-4 w-4" strokeWidth={2} fill="none" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => { onRun(id); setShowMenu(false); }}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${
                    darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  <Play className="h-4 w-4" strokeWidth={2} fill="none" />
                  <span>Run Now</span>
                </button>
                <button
                  onClick={() => { handleViewResults(); setShowMenu(false); }}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${
                    darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" strokeWidth={2} fill="none" />
                  <span>Results</span>
                </button>
                <button
                  onClick={() => { onDelete(id); setShowMenu(false); }}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${
                    darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={2} fill="none" />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center space-x-3 mb-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
            darkMode ? 'bg-gray-700/50' : 'bg-gray-100'
          }`}>
            <Activity className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} strokeWidth={2} fill="none" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} title={name}>{name}</h3>
            {description && <p className={`text-sm line-clamp-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className={`rounded-xl p-3 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Interval</div>
            <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatInterval(intervalSeconds)}</div>
          </div>
          <div className={`rounded-xl p-3 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Run</div>
            <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatLastChecked(lastRun)}</div>
          </div>
        </div>

        {lastError && (
          <div className={`mb-4 p-3 rounded-xl text-xs border ${
            darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <strong>Last error:</strong> {lastError.substring(0, 100)}...
          </div>
        )}

        <div className={`border-t pt-4 mt-2 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className={`flex items-center space-x-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Settings className="h-3 w-3" strokeWidth={2} fill="none" />
            <span>Notifications:</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {notifyEmail && (
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                darkMode ? 'bg-green-900/40 text-green-400 border border-green-500/50' : 'bg-green-100 text-green-700'
              }`}>
                <Mail className="h-3 w-3" strokeWidth={2} fill="none" />
                <span>Email</span>
              </div>
            )}
            {notifyTelegram && (
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                darkMode ? 'bg-sky-900/40 text-sky-400 border border-sky-500/50' : 'bg-blue-100 text-blue-700'
              }`}>
                <MessageCircle className="h-3 w-3" strokeWidth={2} fill="none" />
                <span>Telegram</span>
              </div>
            )}
            {!notifyEmail && !notifyTelegram && (
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>None</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SyntheticCard;